import React, { useState, useEffect, useContext } from 'react';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Description from '@material-ui/icons/Description';
import Skeleton from '@material-ui/lab/Skeleton';
import { colors } from '@skyslope/mache';
import { useHistory } from 'react-router';
import useUserInfo from '../auth/useUserInfo';
import { canSignInPerson, isStartSafeAccount } from '../common/inPersonSigning';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../common/launchDarkly';
import SkeletonTable from '../common/loading/SkeletonTable';
import type { IRecipient } from '../common/recipients/types';
import FooterLinks from '../components/common/FooterLinks';
import ManagementHeader from '../components/common/ManagementHeader';
import NoResultsState from '../components/common/NoResultsState';
import Search from '../components/common/Search';
import Table from '../components/common/Table';
import EmptyState from '../components/envelopeManagement/EmptyState';
import FilterStatus from '../components/envelopeManagement/FilterStatus';
import NewEnvelopeButton from '../components/envelopeManagement/NewEnvelopeButton';
import PdfPreview from '../components/envelopeManagement/PdfPreview';
import RecipientsCell from '../components/envelopeManagement/RecipientsCell';
import StatusActionCell from '../components/envelopeManagement/StatusActionCell';
import StatusProgress from '../components/envelopeManagement/StatusProgress';
import { STATUSES } from '../components/envelopeManagement/utils';
import { HeaderContext } from '../context/header-context';
import useBottomScrollListener from '../hooks/useBottomScrollListener';
import { ResourceNames, EnvelopeStatuses } from '../lib/constants';
import { signerUiUrl } from '../lib/environment-config';
import { useDispatch, useShallowSelector } from '../lib/reduxHooks';
import { UserOrigins } from '../lib/types';
import useDebounce from '../lib/useDebounce';
import type { IRootState } from '../store';
import * as actions from '../store/envelopeManagement/actions';
import { type IEnvelopeMetadata, EnvelopeStatus } from '../store/envelopeManagement/types';
import { setLoading } from '../store/pageFrame/actions';
import type { ISettings } from '../store/pageFrame/types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: 1200,
    width: '100%',
    margin: `${theme.spacing(8)}px ${theme.spacing(2)}px 0`,
    display: 'flex',
    flexDirection: 'column',
  },
  tableControls: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    alignItems: 'center',
  },
  templateCount: {
    display: 'flex',
    alignItems: 'center',
  },
  templateCountIcon: {
    marginRight: theme.spacing(1),
    color: colors.grey[500],
  },
  plusIcon: {
    margin: '-2px 0px -2px -6px',
    height: 18,
  },
  statusActionButton: {
    padding: 6,
    margin: -7,
  },
  statusLinks: {
    display: 'flex',
    marginTop: 8,
  },
  statusLink: {
    padding: '6px 15px 4px 15px',
    margin: '4px 2px 1px 0',
    position: 'relative',
    '&:hover,&:focus': {
      cursor: 'pointer',
      backgroundColor: colors.blue[50],
      borderTopRightRadius: '15px',
      borderTopLeftRadius: '15px',
      outline: 'none',
    },
    userSelect: 'none',
  },
  statusLinkSelected: {
    backgroundColor: colors.blue[800],
    height: '3px',
    position: 'absolute',
    left: '0',
    bottom: '0',
    right: '0',
    margin: '0 14px',
    borderRadius: '2px',
  },
  divider: {
    height: 1,
    width: '100%',
    marginTop: 40,
    marginBottom: 32,
    backgroundColor: colors.grey[400],
  },
  legacySpinner: {
    flexGrow: 1,
    marginLeft: theme.spacing(15),
  },
  foundSkeleton: {
    width: 200,
  },
}));

interface IState {
  init: boolean;
  envelopes: IEnvelopeMetadata[];
  totalEnvelopeCount: number;
  isLoading: boolean;
  envelopeRedirect?: IEnvelopeMetadata;
  endReached: boolean;
  pdfPreview?: IEnvelopeMetadata;
  orderBy: string;
  orderByDesc: boolean;
  selectedStatus: string;
  settings: ISettings;
  settingsFetched: boolean;
  signerToken: string;
  recipients?: IRecipient[];
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selector = (state: IRootState) => ({
  init: state.envelopeManagement.init,
  envelopes: state.envelopeManagement.envelopes,
  totalEnvelopeCount: state.envelopeManagement.totalEnvelopeCount,
  isLoading: state.pageFrame.loading,
  envelopeRedirect: state.envelopeManagement.envelopeRedirect,
  endReached: state.envelopeManagement.endReached,
  pdfPreview: state.envelopeManagement.pdfPreview,
  orderBy: state.envelopeManagement.orderBy,
  orderByDesc: state.envelopeManagement.orderByDesc,
  selectedStatus: state.envelopeManagement.status,
  settings: state.pageFrame.settings,
  settingsFetched: state.pageFrame.settingsFetched,
  signerToken: state.senderBuild.signerToken,
});

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const EnvelopeManagement = ({ flags }: { flags: LaunchDarklyFlags }) => {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const store: IState = useShallowSelector(selector);
  const userInfo = useUserInfo();
  const { senderFirstName, senderLastName, senderEmail, isPrimeAuthenticated, provisioned, isDelegate } =
    userInfo ?? {};
  const canSignInPersonHere = canSignInPerson(
    flags,
    userInfo,
    isStartSafeAccount(store.settings, store.settingsFetched)
  );
  const headerContext = useContext(HeaderContext);
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 200);
  const ignoreFirstLoad = React.useRef(true);
  const [showBottomLoader, setShowBottomLoader] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [orderByState, setOrderBy] = useState('DATE_UPDATED');
  const [orderByDescState, setOrderByDesc] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [resetTableScroll, setResetTableScroll] = useState(false);
  const [hasEnvelopes, setHasEnvelopes] = useState(false);
  const [showSearch, setShowSearch] = useState(true);
  // This hook prevents the rendering of an empty state when the user backspaces the search input until it is empty
  const [alreadySearched, setAlreadySearched] = useState(false);
  const [primeUser, setPrimeUser] = useState(false);
  const showEditEnvelopeName = flags && flags['show-edit-envelope-name'];
  const sender = {
    firstName: senderFirstName,
    lastName: senderLastName,
    email: senderEmail,
  };

  const filteredEnvelopes = statusFilter
    ? // @ts-expect-error -- This code was written prior to enforcing lint rules
      store.envelopes.filter((envelope) => STATUSES[statusFilter].statuses.includes(envelope.status))
    : store.envelopes;

  useEffect(() => {
    if (store.signerToken.length) {
      window.location.href = `${signerUiUrl}?signNow=true#t=${store.signerToken}`;
    }
  }, [store.signerToken]);

  useEffect(() => {
    if (provisioned) {
      fetchMoreEnvelopes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [provisioned]);

  const statusTabs = hasEnvelopes ? (
    <div className={classes.statusLinks}>
      {Object.keys(STATUSES).map((status, index) =>
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        STATUSES[status].dateColumn ? (
          <Typography
            component="span"
            key={status}
            tabIndex={index + 1}
            className={`${classes.statusLink} `}
            onClick={(e: React.MouseEvent<HTMLParagraphElement>) => {
              handleSetSelectedStatus(status);
              e.currentTarget.blur();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSetSelectedStatus(status);
            }}
            variant={selectedStatus === status ? 'body2' : 'body1'}
            data-spec={`status-button-${status}`}
          >
            {status}
            {selectedStatus === status ? <div className={classes.statusLinkSelected} /> : null}
          </Typography>
        ) : null
      )}
    </div>
  ) : null;

  useEffect(() => {
    if (store.settingsFetched) {
      const isPrimeUser = store.settings.account.userOrigin === UserOrigins.Prime;
      if (isPrimeUser && isPrimeAuthenticated) {
        setPrimeUser(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.settingsFetched, isPrimeAuthenticated]);

  useEffect(() => {
    headerContext.setContent(
      <ManagementHeader resource={ResourceNames.ENVELOPE} flags={flags} userOrigin={store.settings?.account.userOrigin}>
        {statusTabs}
      </ManagementHeader>
    );
    return () => {
      headerContext.setContent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [hasEnvelopes, selectedStatus, store.settingsFetched]);

  useEffect(() => {
    return () => {
      dispatch(actions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  useEffect(() => {
    if (ignoreFirstLoad.current) {
      ignoreFirstLoad.current = false;
    } else {
      setResetTableScroll(true);
      dispatch(setLoading(true));
      let { orderBy, orderByDesc } = store;
      if (selectedStatus !== store.selectedStatus) {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        orderBy = STATUSES[selectedStatus]?.dateColumn?.sortKey || 'DATE_UPDATED';
        orderByDesc = true;
        setOrderBy(orderBy);
        setOrderByDesc(orderByDesc);
      }
      dispatch(actions.fetchEnvelopes(debouncedSearch, selectedStatus, orderBy, orderByDesc));
      setResetTableScroll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [debouncedSearch, selectedStatus]);

  useEffect(() => {
    if (store.envelopes.length > 0 && !hasEnvelopes) {
      setHasEnvelopes(true);
    }
    if (showBottomLoader) {
      setShowBottomLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.envelopes.length, store.endReached]);

  useEffect(() => {
    if (statusFilter) {
      setStatusFilter('');
    }
    if (alreadySearched) {
      setAlreadySearched(false);
    }
    if (!showSearch) {
      setShowSearch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [selectedStatus]);

  const fetchMoreEnvelopes = React.useCallback(() => {
    setShowBottomLoader(true);
    dispatch(actions.fetchEnvelopes(debouncedSearch, selectedStatus, orderByState, orderByDescState));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [debouncedSearch, selectedStatus, orderByState, orderByDescState]);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const changeSort = (orderBy: string) => {
    let orderByDesc = true;

    if (store.orderBy === orderBy) {
      orderByDesc = !store.orderByDesc;
    }
    setResetTableScroll(true);
    dispatch(setLoading(true));
    setOrderBy(orderBy);
    setOrderByDesc(orderByDesc);
    dispatch(actions.fetchEnvelopes(debouncedSearch, selectedStatus, orderBy, orderByDesc));
  };

  const tableColumns = [
    { key: showEditEnvelopeName ? 'name' : 'subject', label: 'Envelope Name', boldText: true, sortKey: 'NAME' },
    {
      key: 'recipientsCount',
      label: 'Recipients',
      cell: (envelope: IEnvelopeMetadata) => <RecipientsCell envelope={envelope} />,
    },
    {
      key: 'status',
      label: 'Status',
      sortKey: 'STATUS',
      cell: (envelope: IEnvelopeMetadata) => (
        <StatusProgress
          status={envelope.status}
          signerCount={envelope.signerCount}
          completedSignerCount={envelope.completedSignerCount}
          sentDate={envelope.sentDate}
        />
      ),
    },
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    STATUSES[selectedStatus].dateColumn,
    {
      key: 'statusAction',
      align: 'right',
      label: '',
      cell: (envelope: IEnvelopeMetadata) => (
        <StatusActionCell
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          sender={sender}
          canSignInPerson={canSignInPersonHere}
          isDelegate={isDelegate}
          envelope={envelope}
          flags={flags}
        />
      ),
    },
  ];

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const rowClickHandler = (envelope: IEnvelopeMetadata) => {
    const status = envelope.status.toLowerCase();
    if (status === EnvelopeStatus.DRAFT || status === EnvelopeStatus.CORRECTING) {
      const pathPrefix = envelope.config?.senderUi?.envelopePathPrefix;
      if (pathPrefix) {
        history.push(`/${pathPrefix}envelopes/${envelope.id}/documents`);
      } else {
        history.push(`/envelopes/${envelope.id}/documents`);
      }
    } else {
      dispatch(actions.openPdfPreview(envelope));
    }
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const closePdfPreview = () => {
    dispatch(actions.closePdfPreview());
  };

  if (store.envelopeRedirect) {
    const pathPrefix = store.envelopeRedirect!.config?.senderUi?.envelopePathPrefix;
    if (pathPrefix) {
      window.location.pathname = `/${pathPrefix}envelopes/${store.envelopeRedirect!.id}/documents`;
    } else {
      history.push(`/envelopes/${store.envelopeRedirect!.id}/documents`);
    }
  }

  useBottomScrollListener(() => {
    if (!store.endReached) {
      fetchMoreEnvelopes();
    }
  });

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const searchContent = () => {
    return (
      <>
        {/* @ts-expect-error -- This code was written prior to enforcing lint rules */}
        {STATUSES[selectedStatus].showFilter ? (
          <FilterStatus
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            statuses={STATUSES[selectedStatus].statuses}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            selectedStatus={selectedStatus}
          />
        ) : null}
        <div className={classes.divider} />
        <div className={classes.tableControls}>
          <Typography variant="overline" className={classes.templateCount} data-spec="template-count">
            <Description className={classes.templateCountIcon} />
            {statusFilter
              ? `${filteredEnvelopes.length} ENVELOPE${filteredEnvelopes.length !== 1 ? 'S' : ''} FOUND (FILTERED)`
              : `${store.totalEnvelopeCount.toLocaleString()} ENVELOPE${
                  store.totalEnvelopeCount !== 1 ? 'S' : ''
                } FOUND`}
          </Typography>
          <NewEnvelopeButton isPrimeAuthenticated={isPrimeAuthenticated} isPrimeUser={primeUser} />
        </div>
      </>
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const resultsContent = () => {
    if (filteredEnvelopes.length > 0 || statusFilter) {
      if (!showSearch) {
        setShowSearch(true);
      }
      return (
        <Table
          columns={tableColumns}
          rows={filteredEnvelopes}
          idKey="id"
          showBottomLoader={showBottomLoader}
          resetScroll={resetTableScroll}
          onClickRow={rowClickHandler}
          onClickHeader={changeSort}
          currentSortKey={store.orderBy}
          currentSortDesc={store.orderByDesc}
          managementTable
        />
      );
    }
    if (search) {
      return <NoResultsState resource={ResourceNames.ENVELOPE} />;
    }
    return null;
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const contentToDisplay = () => {
    if (!store.isLoading && store.init && store.settingsFetched) {
      if (filteredEnvelopes.length || search || statusFilter) {
        return (
          <>
            {searchContent()}
            {resultsContent()}

            {store.pdfPreview ? (
              <PdfPreview envelopeId={store.pdfPreview.id} subject={store.pdfPreview.subject} close={closePdfPreview} />
            ) : (
              ''
            )}
            <FooterLinks showSocialIcons />
          </>
        );
      }
      if (filteredEnvelopes.length || alreadySearched) {
        return null;
      }

      if (showSearch) {
        setShowSearch(false);
      }
      return (
        <>
          {/* @ts-expect-error -- This code was written prior to enforcing lint rules */}
          <EmptyState selectedStatus={EnvelopeStatuses[selectedStatus.toUpperCase()]} data-spec="createFirstEnvelope" />
          <FooterLinks showSocialIcons />
        </>
      );
    }
    return (
      <>
        <div data-spec="skeleton-loading-table">
          <div className={classes.divider} />
          <div className={classes.tableControls}>
            <Typography variant="overline" className={classes.templateCount} data-spec="template-count">
              <Description className={classes.templateCountIcon} />
              <span className={classes.foundSkeleton}>
                <Skeleton animation="wave" />
              </span>
            </Typography>
            <NewEnvelopeButton disabled />
          </div>
          <SkeletonTable columns={tableColumns} />
        </div>
      </>
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSearchChange = (value: string) => {
    if (!alreadySearched && value !== '') {
      setAlreadySearched(true);
    }
    setSearch(value);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSetSelectedStatus = (status: string) => {
    setAlreadySearched(false);
    setSelectedStatus(status);
  };

  return (
    <div className={classes.root}>
      {showSearch ? <Search value={search} onChange={handleSearchChange} /> : null}
      {contentToDisplay()}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(EnvelopeManagement);
